import { ReactNode } from "react";
import { CheckCircle2Icon, XCircleIcon, AlertTriangleIcon, ClockIcon } from "lucide-react";

interface SetupCheckRow {
    key: "mic" | "camera" | "network";
    ok: boolean;
    /**
     * Visual variant when `ok` is false. "error" (default) shows a red X;
     * "warning" shows a yellow warning triangle. Ignored when `ok` is true.
     */
    variant?: "error" | "warning";
    title: ReactNode;
    subtitle: ReactNode;
    onEnable?: () => void;
    enableLabel?: ReactNode;
}

interface SetupChecksPanelProps {
    title: ReactNode;
    rows: SetupCheckRow[];
    timeToJoinLabel?: ReactNode;
    timeToJoinElement?: ReactNode;
    /** Replaces the "All set" / "X to fix" badge when provided. */
    statusElement?: ReactNode;
}

export default function SetupChecksPanel({
    title,
    rows,
    timeToJoinLabel,
    timeToJoinElement,
    statusElement,
}: SetupChecksPanelProps) {
    // Warnings are advisory — only hard errors block proceeding, so the
    // "to fix" counter (and the green "All set" branch) ignores them.
    const toFix = rows.filter((r) => !r.ok && r.variant !== "warning").length;

    return (
        <div className="w-full rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1.5 sm:px-4 sm:pt-4 sm:pb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {title}
                </span>
                <div className="flex items-center gap-3">
                    {statusElement}
                    {toFix > 0 ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                            {toFix} to fix
                        </span>
                    ) : (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            All set
                        </span>
                    )}
                </div>
            </div>

            {/* Rows */}
            <ul className="divide-y divide-gray-100 dark:divide-gray-800 m-0">
                {rows.map((row) => (
                    <li key={row.key} className="flex items-center gap-2 sm:gap-3 pl-0 pr-4 py-3">
                        <span className="flex-shrink-0">
                            {row.ok ? (
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-50 dark:bg-green-900/30">
                                    <CheckCircle2Icon className="h-5 w-5 text-green-500" strokeWidth={2} />
                                </span>
                            ) : row.variant === "warning" ? (
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-900/30">
                                    <AlertTriangleIcon className="h-5 w-5 text-amber-500" strokeWidth={2} />
                                </span>
                            ) : (
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/30">
                                    <XCircleIcon className="h-5 w-5 text-red-500" strokeWidth={2} />
                                </span>
                            )}
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {row.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {row.subtitle}
                            </p>
                        </div>
                        {!row.ok && row.onEnable && (
                            <button
                                type="button"
                                onClick={row.onEnable}
                                className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                            >
                                {row.enableLabel ?? "Enable"}
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {/* Time to join row */}
            {timeToJoinElement && (
                <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 px-4 py-2 sm:px-4 sm:py-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <ClockIcon className="h-4 w-4" />
                        <span>{timeToJoinLabel ?? "Time to join"}</span>
                    </div>
                    <div className="text-sm font-mono font-bold text-gray-900 dark:text-white">
                        {timeToJoinElement}
                    </div>
                </div>
            )}
        </div>
    );
}
